import { CardholderSelectStore, CardholderSelectStoreImpl } from "./CardholderSelectStore";
import { v4 as uuidv4 } from "uuid";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import { TrackingActions } from "../actions/TrackingActions";
import { LazyStore } from "../utils/Store";
import { CreditCheckInfo } from "./CheckoutTypes";
import { isMetroCreditAllowance } from "../utils/CheckoutUtil";
import { CustomerDataStore } from "./CustomerDataStore";
import type { ICustomerDataStore } from "./ICustomerDataStore";
import type { RealPaymentType } from "./CheckoutTypes";
import { shouldShowCreditLimitToCustomer } from "../utils/featureFlagUtil";
import * as PaymentMethodsServerRequests from "../server/PaymentMethodsServerRequests";
import type { CheckoutCustomer } from "../model/CheckoutData";
export type CreditCheckStatus = "IDLE" | "LOADING" | "FAILED" | "AVAILABLE" | "DISABLED";
export type CreditCheckStoreState = {
	creditCheckInfo: CreditCheckInfo | null | undefined;
	showCreditCheck: boolean;
	creditCheckStatus: CreditCheckStatus;
	customerCheckoutBlocked?: boolean;
	customerId: string | null | undefined;
	hasMetroCreditPayment: boolean;
	cardholderId: string | null | undefined;
};
export const MAX_CREDIT_CHECK_RETRIES = 2;
const MAX_DELAY_MS = 5000;
export class CreditCheckStoreImpl extends LazyStore {
	_state!: CreditCheckStoreState;
	_timerId: any;
	_customerDataStore: ICustomerDataStore;
	_cardholderSelectStore: CardholderSelectStoreImpl;

	constructor(store: ICustomerDataStore, cardholderSelectStore: CardholderSelectStoreImpl) {
		super();

		this._resetState();

		this._timerId = null;
		this._customerDataStore = store;
		this._cardholderSelectStore = cardholderSelectStore;
	}

	onArm = () => {
		this.listenTo(this._customerDataStore, () => this.onCustomerDataStoreStateUpdate());
		this.listenTo(this._cardholderSelectStore, () => this.onCustomerDataStoreStateUpdate());
		this.onCustomerDataStoreStateUpdate();
	};
	onDisarm = () => {
		this._resetState();
	};

	static isOverLimitIncluding(creditCheckInfo: CreditCheckInfo | null | undefined, cartTotalGrossAmount: number) {
		return !!(
			creditCheckInfo &&
			creditCheckInfo.limit &&
			creditCheckInfo.usedLimit &&
			creditCheckInfo.limit.amount - creditCheckInfo.usedLimit.amount < cartTotalGrossAmount
		);
	}

	isLoadingOrAvailable(): boolean {
		const customerData = this._customerDataStore.getCustomer();

		return (
			["AVAILABLE", "LOADING"].includes(this._state.creditCheckStatus) &&
			!!customerData &&
			customerData.customerId === this._state.customerId
		);
	}

	hasFailedToPollCustomerData(): boolean {
		const customerData = this._customerDataStore.getCustomer();

		return (
			this._state.creditCheckStatus === "FAILED" &&
			!!customerData &&
			customerData.customerId === this._state.customerId
		);
	}

	onCustomerDataStoreStateUpdate(): any {
		const customerData = this._customerDataStore.getCustomer();

		const selectedCardholder = this._cardholderSelectStore.getSelectedCardholder();

		const cardholderData = customerData && selectedCardholder && customerData.cardholderById[selectedCardholder];

		if (!customerData || !selectedCardholder || !cardholderData) {
			return;
		}

		if (
			customerData.checkoutBlockingReasons?.includes("SUSPEND_SELLING") ||
			customerData.cardholderById[selectedCardholder]?.checkoutBlockingReasons?.includes("SIGN_PERSON_TERMS")
		) {
			this._state = {
				...this._state,
				customerCheckoutBlocked: true,
			};
			this.trigger();
			return;
		} else {
			this._state = {
				...this._state,
				customerCheckoutBlocked: false,
			};
		}

		if (
			this._state.cardholderId === selectedCardholder &&
			(this.isLoadingOrAvailable() || this.hasFailedToPollCustomerData())
		) {
			return;
		}

		const customerId = customerData.customerId;
		const payments = cardholderData.paymentsForCardholder;

		if (this.hasMetroCreditPayment(payments)) {
			this.initiateCreditCheckPolling(customerData, selectedCardholder);
			return;
		}

		if (this._state.customerId !== customerId || this._state.creditCheckStatus !== "IDLE") {
			this._state = {
				...this._state,
				showCreditCheck: false,
				creditCheckStatus: "IDLE",
				customerId,
				cardholderId: cardholderData.cardHolderNumber,
			};
			this.trigger();
		}
	}

	shouldDisplayCreditLimit(): boolean {
		return SessionStore.isEmployee() || shouldShowCreditLimitToCustomer();
	}

	hasMetroCreditPayment(payments: Array<RealPaymentType>) {
		return payments && payments.some((p) => isMetroCreditAllowance(p.paymentAllowance));
	}

	initiateCreditCheckPolling(customerData: CheckoutCustomer, selectedCardholder: string) {
		this._state = {
			...this._state,
			showCreditCheck: this.shouldDisplayCreditLimit(),
			creditCheckStatus: "LOADING",
			customerId: customerData.customerId,
			hasMetroCreditPayment: true,
			cardholderId: selectedCardholder,
		};
		this.trigger();
		this.pollCreditCheck(customerData, uuidv4(), 0).catch((error) => {
			console.error("pollCreditCheck in CreditCheckStore failed with ", error);
		});
	}

	pollCreditCheck = async (customerData: CheckoutCustomer, jobId: string, retryCount: number) => {
		if (retryCount === 0) {
			TrackingActions.trackCreditCheck("creditCheck.started", undefined, jobId);
		}

		const creditCheckInfo: CreditCheckInfo | null | undefined = await this.fetchCreditCheck(
			SessionStore.getCountry() || "",
			customerData.customerId,
			customerData.customerNumber,
			customerData.homeStore,
		);

		// Only show credit check if it is still the same customer
		if (this._state.customerId !== customerData.customerId) {
			TrackingActions.trackCreditCheck("creditCheck.aborted", retryCount, jobId);
			return;
		}

		if (creditCheckInfo && creditCheckInfo.timestampLastReceive) {
			this._state = { ...this._state, creditCheckInfo, creditCheckStatus: "AVAILABLE" };
			this.trigger();
			TrackingActions.trackCreditCheck("creditCheck.success", retryCount, jobId);
			return;
		}

		if (retryCount >= MAX_CREDIT_CHECK_RETRIES) {
			this._state = { ...this._state, creditCheckStatus: "FAILED" };
			this.trigger();
			TrackingActions.trackCreditCheck("creditCheck.failed", retryCount, jobId);
			return;
		}

		this._timerId = setTimeout(() => this.pollCreditCheck(customerData, jobId, retryCount + 1), MAX_DELAY_MS);
	};

	async fetchCreditCheck(
		country: string,
		customerId: string,
		customerNumber?: string | null,
		homeStore?: string | null,
	): Promise<CreditCheckInfo | null | undefined> {
		try {
			return await PaymentMethodsServerRequests.getCreditCheck(country, customerId, customerNumber, homeStore);
		} catch (e) {
			return null;
		}
	}

	getState(): any {
		const {
			creditCheckStatus,
			creditCheckInfo,
			customerCheckoutBlocked,
			customerId,
			showCreditCheck,
			hasMetroCreditPayment,
		} = this._state;

		if (!hasMetroCreditPayment) {
			return {
				creditCheckStatus: "DISABLED",
				creditCheckInfo: null,
				showCreditCheck: false,
				customerCheckoutBlocked,
				customerId,
				hasMetroCreditPayment: false,
			};
		}

		return {
			creditCheckStatus,
			creditCheckInfo,
			showCreditCheck: !!showCreditCheck,
			customerCheckoutBlocked,
			customerId,
			hasMetroCreditPayment: true,
		};
	}

	_clearTimer() {
		if (this._timerId) {
			clearTimeout(this._timerId);
			this._timerId = null;
		}
	}

	_resetState() {
		this._clearTimer();

		this._state = {
			creditCheckInfo: null,
			showCreditCheck: false,
			creditCheckStatus: "IDLE",
			customerCheckoutBlocked: false,
			customerId: null,
			hasMetroCreditPayment: false,
			cardholderId: null,
		};
	}
}
export const CreditCheckStore = new CreditCheckStoreImpl(CustomerDataStore, CardholderSelectStore);
